import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

// 状态 → 甘特条颜色
const STATUS_COLOR = {
  pending: '#d9d9d9',
  processing: '#1677ff',
  completed: '#52c41a',
};

const STATUS_LABEL = {
  pending: '待开始',
  processing: '进行中',
  completed: '已完成',
};

const MILESTONE_COLOR = '#fa8c16';

// ══════════════════════════════════════════════
//  服务排期甘特图（echarts custom series）
//  records: [{ key, content, start, end, ownerName, status, milestone }]
// ══════════════════════════════════════════════

const ServiceGantt = ({ records = [], xMin, xMax, height = 340 }) => {
  const domRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!domRef.current) return undefined;
    if (!chartRef.current) {
      chartRef.current = echarts.init(domRef.current);
    }
    const chart = chartRef.current;

    const categories = records.map((r) => r.content);
    const data = records.map((r, idx) => ({
      value: [idx, r.start, r.end, 0, r.milestone ? 1 : 0],
      itemStyle: { color: r.milestone ? MILESTONE_COLOR : (STATUS_COLOR[r.status] || '#1677ff') },
      raw: r,
    }));

    const option = {
      tooltip: {
        formatter: (params) => {
          const r = params.data && params.data.raw;
          if (!r) return '';
          return [
            `<b>${r.content}</b>`,
            `时间：${r.start} ~ ${r.end}`,
            `责任人：${r.ownerName || '-'}`,
            `状态：${STATUS_LABEL[r.status] || '-'}`,
            r.milestone ? '<span style="color:#fa8c16">◆ 里程碑</span>' : '',
          ].filter(Boolean).join('<br/>');
        },
      },
      grid: { left: 8, right: 28, top: 20, bottom: 8, containLabel: true },
      xAxis: {
        type: 'time',
        min: xMin,
        max: xMax,
        axisLine: { lineStyle: { color: '#d9d9d9' } },
        axisLabel: { color: '#8c8c8c', formatter: '{MM}-{dd}' },
        splitLine: { show: true, lineStyle: { color: '#f0f0f0' } },
      },
      yAxis: {
        type: 'category',
        data: categories,
        inverse: true,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#d9d9d9' } },
        axisLabel: { color: '#595959', width: 150, overflow: 'truncate' },
      },
      series: [
        {
          type: 'custom',
          encode: { x: [1, 2], y: 0 },
          data,
          renderItem: (params, api) => {
            const categoryIdx = api.value(0);
            const start = api.coord([api.value(1), categoryIdx]);
            const end = api.coord([api.value(2), categoryIdx]);
            const isMilestone = api.value(4) === 1;

            if (isMilestone) {
              const r = 7;
              return {
                type: 'polygon',
                shape: {
                  points: [
                    [start[0], start[1] - r],
                    [start[0] + r, start[1]],
                    [start[0], start[1] + r],
                    [start[0] - r, start[1]],
                  ],
                },
                style: api.style(),
              };
            }

            const bandHeight = api.size([0, 1])[1];
            const barHeight = Math.max(8, bandHeight * 0.5);
            const rect = echarts.graphic.clipRectByRect(
              {
                x: start[0],
                y: start[1] - barHeight / 2,
                width: Math.max(2, end[0] - start[0]),
                height: barHeight,
              },
              {
                x: params.coordSys.x,
                y: params.coordSys.y,
                width: params.coordSys.width,
                height: params.coordSys.height,
              }
            );
            return rect && { type: 'rect', shape: rect, style: api.style() };
          },
        },
      ],
    };

    chart.setOption(option, true);
    return undefined;
  }, [records, xMin, xMax, height]);

  useEffect(() => {
    const handleResize = () => chartRef.current && chartRef.current.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, []);

  return <div ref={domRef} style={{ width: '100%', height }} />;
};

export default ServiceGantt;
